import React from 'react';
import { ResultBanner } from './ResultBanner';
import { ResultActions } from './ResultActions';
import { ScoreCard } from './ScoreCard';
import { Level } from './Level';
import XPBar from './XPBar';

const wrapperBase =
  'result-page-wrapper min-h-screen w-full flex items-center justify-center p-4 box-border max-md:p-4 max-md:pb-[100px]';

const containerBase =
  'flex flex-col items-center w-full max-w-[520px] bg-[var(--card-bg)] rounded-[20px] gap-3.5 relative m-0 box-border p-5 max-md:p-5 max-md:px-4 max-md:rounded-2xl max-md:gap-5 max-md:max-w-full';

const containerVariants = {
  default:
    'shadow-[0_8px_32px_rgba(29,71,70,0.2)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)] dark:border dark:border-white/[0.08]',
  drill: 'shadow-[0_8px_32px_rgba(30,61,111,0.2)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)] dark:border dark:border-white/[0.08]',
};

const levelCardBase =
  'w-[100px] h-[100px] rounded-[14px] border-[3px] border-[#fcb319] flex items-center justify-center my-1 mx-auto max-md:w-[120px] max-md:h-[120px] max-md:rounded-2xl';

const levelCardVariants = {
  default:
    'bg-gradient-to-br from-[#1d4746] via-[#236463] to-[#12766d] shadow-[0_6px_20px_rgba(29,71,70,0.25)] dark:from-[#0f2928] dark:via-[#153938] dark:to-[#0d4f49] dark:shadow-[0_6px_20px_rgba(0,0,0,0.4)]',
  drill:
    'bg-gradient-to-br from-[#1e3d6f] via-[#345d8a] to-[#4a6fa5] shadow-[0_6px_20px_rgba(30,61,111,0.25)] dark:from-[#162d50] dark:via-[#1e3d6f] dark:to-[#2e4a6e]',
};

const progressSection =
  'w-full max-w-[420px] py-0 px-1 mx-auto [&_.h-6]:h-5 [&_.h-6]:min-h-5 [&_.h-5]:h-3.5 max-md:[&_.h-6]:h-[18px] max-md:[&_.h-6]:min-h-[18px] max-md:[&_.text-lg]:text-sm max-md:[&_.text-sm]:text-xs max-md:[&_.text-xs]:text-[11px] max-md:[&_.gap-3]:gap-1.5 max-md:[&_.gap-2]:gap-2';

const levelCardOverrides =
  '[&_.text-lg]:!text-[11px] [&_.text-7xl]:!text-[42px] max-md:[&_.text-lg]:!text-xs max-md:[&_.text-7xl]:!text-4xl';

/**
 * Unified result layout for both ResultPage and SkillResultPage.
 * @param {'full' | 'skill'} variant - full: Level + XPBar + ScoreCard; skill: ScoreCard only
 */
export function ResultLayout({
  variant = 'full',
  language = 'German language learning',
  courseType = 'Course Practice',
  level = 'All Levels',
  subcourseName = null,
  lessonName = null,
  practiceTypeLabel = null,
  subPracticeTypeLabel = null,
  subpracticeTypeLabel = null,
  score = 0,
  totalQuestions,
  correctCount,
  currentLevel,
  previousLevel,
  deltaLevel,
  onBack,
  onContinue,
  onReview,
}) {
  const isDrillVocab = courseType === 'Drill Vocab';
  const isFull = variant === 'full';
  const v = isDrillVocab ? 'drill' : 'default';
  const wrapperClass = `${wrapperBase}${isDrillVocab ? ' vocab-drill-result' : ''}${!isFull ? ' result-page-wrapper--skill' : ''}`;

  return (
    <div className={wrapperClass}>
      <div className={`${containerBase} ${containerVariants[v]}`}>
        <ResultBanner
          language={language}
          courseType={courseType}
          subcourseName={subcourseName}
          lessonName={lessonName}
          practiceTypeLabel={practiceTypeLabel}
          subPracticeTypeLabel={subPracticeTypeLabel}
          subpracticeTypeLabel={subpracticeTypeLabel}
        />

        {isFull && (
          <>
            <div className={`${levelCardBase} ${levelCardVariants[v]} ${levelCardOverrides}`}>
              <Level />
            </div>
            <div className={progressSection}>
              <XPBar
                current_level={currentLevel}
                previous_level={previousLevel}
                delta_level={deltaLevel}
              />
            </div>
          </>
        )}

        <div className="w-full max-w-[420px] mx-auto">
          <ScoreCard
            score={score}
            totalQuestions={totalQuestions}
            correctCount={correctCount}
            variant={variant}
            isDrillVocab={isDrillVocab}
          />
        </div>

        <ResultActions
          onBack={onBack}
          onContinue={onContinue}
          onReview={onReview}
          isDrillVocab={isDrillVocab}
        />
      </div>
    </div>
  );
}
